// @ts-strict-ignore
import { TranslateService } from '@ngx-translate/core';
import { LocaleLanguage } from '@user/components/language/locale-language';
import { Dictionary } from 'typescript-collections';
import { Observable, Subject, Subscription } from 'rxjs';
import { StringUtils } from '@shared/util/string-utils';
import { CookieStorage } from '../cookie-storage';
import { Injectable } from '@angular/core';
import { MissingTranslationReporter } from './missing-translation-reporter';
import { Log } from '@shared/debug/log';
import { QueryParamsService } from '@services/query-params/query-params.service';

export type TranslationParams = {[key: string | number]: string | number} | (string|number)[];

@Injectable({ providedIn: 'root' })
export class LocaleResourceManager {
  private readonly RESOURCE_KEY_PREFIX: string = 'key:';
  private readonly _defaultLangCode: string = 'en';

  private _langCodeToLanguageDictionary: Dictionary<string, LocaleLanguage> = new Dictionary<string, LocaleLanguage>();
  private _currentLanguage: LocaleLanguage;

  private _localeChanged$ = new Subject<LocaleLanguage>();
  private _languageChangedSubscription: Subscription;

  constructor(
    private translateService: TranslateService,
    private cookieStorage: CookieStorage,
    private queryParamsService: QueryParamsService
  ) {
    this.translateService.missingTranslationHandler = new MissingTranslationReporter();
    this.initializeLanguages();
    this.translateService.addLangs(this._langCodeToLanguageDictionary.keys());
    this.setInitialLanguage();
  }

  public get localeChanged$(): Observable<LocaleLanguage> {
    return this._localeChanged$.asObservable();
  }

  private initializeLanguages(): void {
    this._langCodeToLanguageDictionary.setValue('en', new LocaleLanguage('en', 'en_US', 'English'));
    this._langCodeToLanguageDictionary.setValue('de', new LocaleLanguage('de', 'de_DE', 'Deutsch / German'));
    this._langCodeToLanguageDictionary.setValue('es', new LocaleLanguage('es', 'es_ES', 'Español / Spanish'));
    this._langCodeToLanguageDictionary.setValue('fr', new LocaleLanguage('fr', 'fr_FR', 'Français / French'));
    this._langCodeToLanguageDictionary.setValue('it', new LocaleLanguage('it', 'it_IT', 'Italiano / Italian'));
    this._langCodeToLanguageDictionary.setValue('pl', new LocaleLanguage('pl', 'pl_PL', 'Polski / Polish'));
    this._langCodeToLanguageDictionary.setValue('pt', new LocaleLanguage('pt', 'pt_BR', 'Português / Portuguese'));
    this._langCodeToLanguageDictionary.setValue('ru', new LocaleLanguage('ru', 'ru_RU', 'Русский / Russian'));
    this._langCodeToLanguageDictionary.setValue('da', new LocaleLanguage('da', 'da_DK', 'Dansk / Danish'));
    this._langCodeToLanguageDictionary.setValue('fi', new LocaleLanguage('fi', 'fi_FI', 'Suomi / Finnish'));
    this._langCodeToLanguageDictionary.setValue('nl', new LocaleLanguage('nl', 'nl_NL', 'Nederlands / Dutch'));
    this._langCodeToLanguageDictionary.setValue('sv', new LocaleLanguage('sv', 'sv_SE', 'Svenska / Swedish'));
    this._langCodeToLanguageDictionary.setValue('cz', new LocaleLanguage('cz', 'cs_CZ', 'Čeština / Czech'));
    this._langCodeToLanguageDictionary.setValue('zh', new LocaleLanguage('zh', 'zh_CN', '中文 / Chinese'));
    this._langCodeToLanguageDictionary.setValue('ja', new LocaleLanguage('ja', 'ja_JP', '日本語 / Japanese'));
  }

  private setInitialLanguage(): void {
    const languageParameter = this.queryParamsService.getParamValue('lang');
    if(languageParameter){
      this.updateCurrentLanguage(languageParameter);
      return;
    }

    this.setLanguageFromBrowserValues();
  }

  private setLanguageFromBrowserValues(): void {
    const browserLangDefault = this.translateService.getBrowserLang();
    const langs = this.translateService.getLangs();
    const lastSelectedLanguage = this.cookieStorage.lastSelectLanguage;
    let initialLangCode: string;
    if (lastSelectedLanguage) {
      initialLangCode = lastSelectedLanguage;
    } else if (langs.indexOf(browserLangDefault) > -1) {
      initialLangCode = browserLangDefault;
    } else {
      initialLangCode = this._defaultLangCode;
    }
    this.updateCurrentLanguage(initialLangCode);
  }

  public updateCurrentLanguage(langCode: string): void {
    this.cookieStorage.lastSelectLanguage = langCode;
    this._currentLanguage = this._langCodeToLanguageDictionary.getValue(langCode);
    this.translateService.setDefaultLang(this._defaultLangCode);
    this.resubscribeLanguageChange(langCode);
    document.documentElement.lang = langCode;
  }

  private resubscribeLanguageChange(langCode: string): void {
    this._languageChangedSubscription?.unsubscribe();
    this._languageChangedSubscription = this.translateService.use(langCode).subscribe(() => this._localeChanged$.next(this._currentLanguage));
  }

  public get currentLanguage(): LocaleLanguage {
    return this._currentLanguage;
  }

  public get defaultLanguage(): LocaleLanguage {
    return this._langCodeToLanguageDictionary.getValue(this.translateService.defaultLang);
  }

  public getLanguages(): LocaleLanguage[] {
    return this._langCodeToLanguageDictionary.values();
  }

  /** Returns the original key if the key is not found for the current language or the fallback language */
  public getValueByKey(key: string, params?: Object, keyMightIntentionallyBeMissing = false): string {
    if(keyMightIntentionallyBeMissing) {
      (this.translateService.missingTranslationHandler as MissingTranslationReporter).deactivated = true;
    }
    let translationValue: string = key;

    try {
      translationValue = this.translateService.instant(key, params)
    } catch (e) {
      Log.error(`Error translating key '${key}'`, e);
    }
    (this.translateService.missingTranslationHandler as MissingTranslationReporter).deactivated = false;
    return translationValue;
  }

  /** Returns null if the key is not found for the current language or the fallback language */
  public findValueByKey(key: string, params?: Object, keyMightIntentionallyBeMissing = false): string | undefined {
    const translatedValue = this.getValueByKey(key, params, keyMightIntentionallyBeMissing);
    return translatedValue == key ? undefined : translatedValue;
  }

  public getValueByKeyAsObservable(key: string, params?: Object): Observable<string> {
    return this.translateService.get(key, params);
  }

  public getLanguageByKey(langKey: string): LocaleLanguage {
    for (const language of this.getLanguages()) {
      if (language.langKey === langKey) {
        return language;
      }
    }
    return null;
  }

  public getLocaleChain(): string[] {
    return [this.currentLanguage.langKey, 'en_US', 'de_DE'];
  }

  public formatNumber(value: number, useThousandsSeparator: boolean = true, precision: number = -1, useMinFractionDigits: boolean = false): string {
    if (precision >= 0) {
      value = +value.toFixed(precision);
    }
    const options: Intl.NumberFormatOptions = {
      useGrouping: useThousandsSeparator,
    };
    if (useMinFractionDigits) {
      options.minimumFractionDigits = precision;
    }
    const formatter = new Intl.NumberFormat(this.currentLanguage.langCode, options);
    return formatter.format(value);
  }

  public parseNumber(str: string): number {
    return StringUtils.parseNumber(
      str,
      this.getValueByKey('validation.number.decimal_separator'),
      this.getValueByKey('validation.number.thousands_separator')
    );
  }

  public getResolvedString(s: string): string {
    if (this.isResourceKey(s)) {
      return this.getValueByKey(this.convertResourceKeyToResourceName(s), null);
    } else {
      return s;
    }
  }

  private isResourceKey(s: string): boolean {
    if (!s) {
      return false;
    }
    return StringUtils.startsWith(s, this.RESOURCE_KEY_PREFIX);
  }

  private convertResourceKeyToResourceName(resourceKey: string): string {
    return StringUtils.removePrefix(resourceKey, this.RESOURCE_KEY_PREFIX);
  }
}
